'use client'

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import debounce from 'lodash.debounce'
import { useSession } from 'next-auth/react'

import {
  CustomerTypes,
  extractCustomerFromClientSession,
  extractTokenFromClientSession
} from 'modules/customer'
import { isPostalCodeAdded } from 'modules/delivery'
import {
  AnyProductTypes,
  avabilityProductCheck,
  helperFloat
} from 'modules/products'
import { SeoListName } from 'shared/analytics'
import {
  OfflineContext,
  OfflineContextType
} from 'shared/ui/Offline/OfflineContext'
import { isNotNil, isNotNilOrEmpty } from 'shared/utils/ramda'

import { addToCartSeo, removeFromCartSeo } from '../../../analytics'
import useCartStore, { useDisplayCart } from '../../../store/cartStore'

interface UseAddToCartProps {
  product: AnyProductTypes
  seoButtonPlace: string
  quantityJump?: number
  cartItemId?: string
  setProductQuantity: (quantity: number) => void
  idxOnList: number
  productQuantity: number
  listName: SeoListName
}

const useAddToCart = ({
  product,
  quantityJump,
  seoButtonPlace,
  cartItemId,
  setProductQuantity,
  idxOnList,
  listName
}: UseAddToCartProps) => {
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const [postalCodeModalOpen, setPostalCodeModalOpen] = useState(false)
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [quantityLimitMet, setQuantityLimitMet] = useState(false)
  const [isSpecialOfferIncrementDisabled, setIsSpecialOfferIncrementDisabled] =
    useState(false)
  const currentAction = useRef<null | (() => void) | (() => Promise<void>)>(
    null
  )
  const token = useMemo(() => extractTokenFromClientSession(session), [session])

  const precision = quantityJump || 1
  const isCartItem = isNotNilOrEmpty(cartItemId)

  const { isAvailable, quantityLimit } = avabilityProductCheck(product)

  const handleRemoveModalOpen = () => setConfirmationModalOpen(true)
  const handleRemoveModalClose = () => setConfirmationModalOpen(false)

  const handleClosePostalCodeModal = () => {
    setPostalCodeModalOpen(false)
  }

  const handleClearCurrentAction = () => {
    currentAction.current = null
  }

  const {
    getProductCount,
    getCartItemCount,
    getRestProductsCount,
    queueCartOperation,
    getAllCartItemsLength,
    getCartItem
  } = useCartStore()

  const cart = useDisplayCart()
  const { isOnline, temporaryOfflineNotificationError } = useContext(
    OfflineContext
  ) as OfflineContextType

  const debouncedCartUpdate = useCallback(
    debounce(() => {
      // placeholder since we're using the context's queueCartOperation
      handleClearCurrentAction()
    }, 1000),
    [handleClearCurrentAction]
  )

  useEffect(() => {
    if (cartItemId) {
      const productCount = getProductCount(product.id)
      const cartItemCount = getCartItemCount(cartItemId)
      const allCartItemsLength = getAllCartItemsLength(product.id)
      const cartItem = getCartItem(cartItemId)

      productCount >= quantityLimit
        ? setQuantityLimitMet(true)
        : setQuantityLimitMet(false)

      isNotNil(product.specialOfferMaxQuantityPerSaleOrder) &&
      isNotNil(cartItem?.specialOfferPriceGross) &&
      allCartItemsLength > 1 &&
      cartItemCount >= product.specialOfferMaxQuantityPerSaleOrder
        ? setIsSpecialOfferIncrementDisabled(true)
        : setIsSpecialOfferIncrementDisabled(false)
    }
  }, [
    cartItemId,
    product.specialOfferMaxQuantityPerSaleOrder,
    cart,
    setProductQuantity
  ])

  useEffect(() => {
    return () => {
      if (debouncedCartUpdate.cancel) {
        debouncedCartUpdate.cancel()
      }
    }
  }, [debouncedCartUpdate])

  const handleQuantity = (quantity: number, productQuantity?: number) => {
    if (cartItemId) {
      if (queueCartOperation) {
        queueCartOperation({
          type: 'update',
          productId: product.id,
          quantity,
          visibleQuantity: productQuantity,
          itemId: cartItemId
        })
      }
    }
  }

  const handleAddToCartSeo = (currentQuantity: number) => {
    const quantityInCart = isCartItem
      ? getCartItemCount(cartItemId)
      : getProductCount(product.id)

    const addedQuantity = currentQuantity - quantityInCart

    const addedProductQuantity = addedQuantity > 0 ? addedQuantity : 1

    addToCartSeo({
      user,
      product,
      button_place: seoButtonPlace,
      idxOnList: idxOnList,
      productQuantity: addedProductQuantity,
      cart,
      listName,
      type: isCartItem ? 'increment' : 'add'
    })
  }

  const handleRemoveFromCartSeo = (quantity: number) => {
    const quantityInCart = isCartItem
      ? getCartItemCount(cartItemId)
      : getProductCount(product.id)

    const removedQuantity = quantityInCart - quantity
    const removedProductQuantity = removedQuantity > 0 ? removedQuantity : 1
    const type = quantity >= quantityInCart ? 'remove' : 'decrement'

    removeFromCartSeo({
      user,
      product,
      button_place: seoButtonPlace,
      idxOnList: idxOnList || 0,
      productQuantity: removedProductQuantity,
      cart,
      listName,
      type
    })
  }

  const addNewItem = (customQuantity?: number, callback?: () => void) => {
    currentAction.current = () => addNewItem(customQuantity, callback)

    if (!isPostalCodeAdded(token)) return setPostalCodeModalOpen(true)

    if (!isOnline) return temporaryOfflineNotificationError()

    const quantity = customQuantity || quantityJump || helperFloat('1')

    setProductQuantity(quantity)
    handleAddToCartSeo(quantity)

    if (queueCartOperation) {
      queueCartOperation({
        type: 'add',
        productId: product.id,
        quantity,
        product: product
      })
    }
    callback?.()
  }

  const handleIncrement = () => {
    currentAction.current = handleIncrement

    if (!isPostalCodeAdded(token)) return setPostalCodeModalOpen(true)

    if (!isOnline) return temporaryOfflineNotificationError()

    const currentTotalQuantity = getProductCount(product.id)
    const incrementAmount = quantityJump || 1
    const newTotalQuantity = Math.min(
      currentTotalQuantity + incrementAmount,
      quantityLimit
    )
    const actualIncrement = newTotalQuantity - currentTotalQuantity

    if (actualIncrement <= 0) return

    setProductQuantity(newTotalQuantity)
    handleAddToCartSeo(newTotalQuantity)

    if (!cartItemId) {
      const cartItems =
        cart?.items?.filter(i => i.product?.id === product.id) || []

      const isTemporaryItem =
        cartItems.length === 1 && cartItems[0].id.startsWith('temp-')

      if (isTemporaryItem && currentTotalQuantity === 1) {
        if (queueCartOperation) {
          queueCartOperation({
            type: 'add',
            productId: product.id,
            quantity: newTotalQuantity,
            product
          })
        }
      } else if (cartItems.length > 0) {
        if (cartItems.length === 1) {
          const item = cartItems[0]

          if (queueCartOperation) {
            queueCartOperation({
              type: 'update',
              productId: product.id,
              quantity: newTotalQuantity,
              itemId: item.id
            })
          }
        } else {
          const firstItem = cartItems[0]

          const otherItemsQuantity = cartItems
            .slice(1)
            .reduce((sum, item) => sum + item.quantity, 0)
          const newFirstItemQuantity = Math.max(
            0,
            newTotalQuantity - otherItemsQuantity
          )
          const fullItemQuantity =
            firstItem.quantity + otherItemsQuantity + precision

          if (queueCartOperation) {
            queueCartOperation({
              type: 'update',
              productId: product.id,
              quantity: fullItemQuantity,
              visibleQuantity: newFirstItemQuantity,
              itemId: firstItem.id
            })
          }
        }
      } else {
        if (queueCartOperation) {
          queueCartOperation({
            type: 'add',
            productId: product.id,
            quantity: newTotalQuantity,
            product
          })
        }
      }
    } else {
      const restProductsCount = getRestProductsCount(product.id, cartItemId)
      const visibleQuantity = newTotalQuantity - restProductsCount

      handleQuantity(newTotalQuantity, visibleQuantity)
    }
  }

  const handleDecrement = () => {
    currentAction.current = handleDecrement

    if (!isPostalCodeAdded(token)) return setPostalCodeModalOpen(true)

    if (!isOnline) return temporaryOfflineNotificationError()

    const currentTotalQuantity = getProductCount(product.id)
    const decrementAmount = quantityJump || 1
    const newTotalQuantity = Math.max(currentTotalQuantity - decrementAmount, 0)

    const actualDecrement = currentTotalQuantity - newTotalQuantity

    if (actualDecrement <= 0) return

    if (newTotalQuantity <= 0) {
      return handleRemoveModalOpen()
    }
    handleRemoveFromCartSeo(actualDecrement)
    setProductQuantity(newTotalQuantity)

    if (!cartItemId) {
      const cartItems =
        cart?.items?.filter(i => i.product?.id === product.id) || []

      if (cartItems.length > 0) {
        if (cartItems.length === 1) {
          const item = cartItems[0]

          if (queueCartOperation) {
            queueCartOperation({
              type: 'update',
              productId: product.id,
              quantity: newTotalQuantity,
              itemId: item.id
            })
          }
        } else {
          const promotionItem = cartItems.find(i => i.promotionId)
          const specialOfferItem = cartItems.find(
            i =>
              i.specialOfferPriceGross !== null &&
              i.product.specialOfferMaxQuantityPerSaleOrder !== null
          )
          const normalItem = cartItems.find(
            i => i.id !== promotionItem?.id && i.id !== specialOfferItem?.id
          )

          const allItemsLength = cartItems.length

          const newFirstItemQuantity = Math.max(
            0,
            newTotalQuantity -
              ((specialOfferItem?.quantity || 0) +
                (promotionItem?.quantity || 0))
          )

          const fullItemQuantity =
            (normalItem?.quantity || 0) +
            (promotionItem?.quantity || 0) +
            (specialOfferItem?.quantity || 0) -
            precision

          if (newFirstItemQuantity > 0) {
            if (queueCartOperation) {
              queueCartOperation({
                type: 'update',
                productId: product.id,
                quantity: fullItemQuantity,
                visibleQuantity: newFirstItemQuantity,
                itemId: normalItem?.id
              })
            }
          }

          if (newFirstItemQuantity === 0 && allItemsLength > 1) {
            const restItemsQuantity = promotionItem?.quantity || 0

            const newSecondItemQuantity = Math.max(
              0,
              newTotalQuantity - restItemsQuantity
            )

            if (queueCartOperation) {
              queueCartOperation({
                type: 'update',
                productId: product.id,
                quantity: fullItemQuantity,
                visibleQuantity: newSecondItemQuantity,
                itemId: specialOfferItem?.id
              })

              queueCartOperation({
                type: 'update',
                productId: product.id,
                quantity: 0,
                visibleQuantity: 0,
                itemId: normalItem?.id,
                preventBatching: true
              })
            }

            if (newSecondItemQuantity === 0 && allItemsLength > 2) {
              const newThirdItemQuantity = Math.max(0, newTotalQuantity)

              if (queueCartOperation) {
                queueCartOperation({
                  type: 'update',
                  productId: product.id,
                  quantity: fullItemQuantity,
                  visibleQuantity: newThirdItemQuantity,
                  itemId: promotionItem?.id
                })

                queueCartOperation({
                  type: 'update',
                  productId: product.id,
                  quantity: 0,
                  visibleQuantity: 0,
                  itemId: specialOfferItem?.id,
                  preventBatching: true
                })

                queueCartOperation({
                  type: 'update',
                  productId: product.id,
                  quantity: 0,
                  visibleQuantity: 0,
                  itemId: normalItem?.id,
                  preventBatching: true
                })
              }
            }
          }
        }
      }
    } else {
      const restProductsCount = getRestProductsCount(product.id, cartItemId)
      const visibleQuantity = newTotalQuantity - restProductsCount

      if (visibleQuantity == 0) {
        handleRemoveModalOpen()
      } else {
        handleQuantity(newTotalQuantity, visibleQuantity)
      }
    }
  }

  const handleSetCustomQuantity = async (quantity: number) => {
    currentAction.current = () => handleSetCustomQuantity(quantity)

    if (!isPostalCodeAdded(token)) return setPostalCodeModalOpen(true)

    if (!isOnline) return temporaryOfflineNotificationError()

    setProductQuantity(quantity)
    handleQuantity(quantity)

    const quantityInCart = getProductCount(product.id)

    quantityInCart > quantity
      ? handleRemoveFromCartSeo(quantity)
      : handleAddToCartSeo(quantity)
  }

  const handleDirectItemUpdate = (itemId: string, newQuantity: number) => {
    if (!isPostalCodeAdded(token)) return setPostalCodeModalOpen(true)
    if (!isOnline) return temporaryOfflineNotificationError()

    setProductQuantity(newQuantity)

    if (queueCartOperation) {
      queueCartOperation({
        type: 'update',
        productId: product.id,
        quantity: newQuantity,
        itemId: itemId
      })
    }
  }

  return {
    confirmationModalOpen,
    handleRemoveModalClose,
    postalCodeModalOpen,
    handleClosePostalCodeModal,
    handleQuantity,
    addNewItem,
    handleIncrement,
    handleDecrement,
    handleSetCustomQuantity,
    handleRemoveFromCartSeo,
    quantityLimit,
    isCartItem,
    isAvailable,
    currentAction: currentAction.current,
    debouncedCartUpdate,
    handleDirectItemUpdate,
    quantityLimitMet,
    isSpecialOfferIncrementDisabled
  }
}

export default useAddToCart
